import type { ComponentChildren } from 'preact';
import * as stylex from '@stylexjs/stylex';
import { styles } from './button-group.styles';
export type ButtonGroupAlign = 'start' | 'center' | 'end' | 'spread';
export interface ButtonGroupProps {
  /** Where the buttons sit in the space available. Table row actions use `end`. */
  align?: ButtonGroupAlign;
  /** Keep the cluster on ONE line. Only for a group inside a horizontal
   *  scroller (a table row), where scrolling is how the operator reaches it.
   *  Anywhere else this squeezes buttons into each other instead of folding. */
  nowrap?: boolean;
  children: ComponentChildren;
}

/**
 * The container every cluster of buttons goes in.
 *
 * Buttons are never emitted as loose siblings: a group is one thing on the
 * screen and it gets one box, so the gap between actions is decided in exactly
 * one place instead of being re-invented as a `display:flex; gap` on every page
 * that happens to need two buttons. That drift is what this replaces — the row
 * actions on one table sat 8px apart and 6px on the next, and neither wrapped.
 *
 * ⚠ It WRAPS by default, and `nowrap` is the opt-in. Both defaults have
 * been tried: a cluster in a table row must stay on one line (the table scrolls
 * sideways to reach it, and folding makes row heights disagree), but a cluster
 * with nowhere to overflow to — a modal footer — squeezes its buttons into each
 * other instead, which is what shipped. Footers outnumber scrollers, so the
 * forgiving case is the default and a table row's action cell opts out.
 */
export function ButtonGroup({
  align = 'start',
  nowrap = false,
  children
}: ButtonGroupProps) {
  return <div {...stylex.props(styles.group, styles[align], nowrap && styles.nowrap)}>{children}</div>;
}